import type { IDeliveryTrackStop } from "@agrigate/location-types";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import ComboboxSelect from "@agrigate/shared-web-ui/lib/ComboSelect";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { Combobox, ComboboxRootProps } from "@chakra-ui/react";
import type { ElementType } from "react";
import { useEffect, useMemo } from "react";
import { useGeoScope } from "../hooks/use-geo-scope";
import rpc from "../utils/rpc";

export default function CollectionCentersDropdown(p: {
    name?: string;
    value?: MaybeN<string>;
    disabled?: boolean;
    width?: string;
    defaultValue?: MaybeN<string>;
    onInit?: (collectionCenter: MaybeN<IDeliveryTrackStop>) => void;
    onChange?: (collectionCenter: MaybeN<IDeliveryTrackStop>) => void;
    loadCCfromClusterId?: boolean;
    regionId?: MaybeN<string>;
    districtId?: MaybeN<string>;
    clusterId?: MaybeN<string>;
    customCaret?: ElementType;
    placement?: ComboboxRootProps["placeContent"];
    portalled?: boolean;
    isDesktop?: boolean;
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
}) {
    const { pinnedIds } = useGeoScope();

    const fetchCollectionCentersT = useAsyncTask(
        async () => {
            const res = await rpc.collectionCenter.findAll({
                regionId: p.regionId,
                districtId: p.districtId,
                clusterId: p.clusterId ?? pinnedIds.clusterId,
            });
            if (p.defaultValue) {
                p.onInit?.(
                    res.collectionCenters.find(_ => _.id === p.defaultValue),
                );
            } else {
                p.onInit?.(null);
            }
            return res.collectionCenters;
        },
        {
            deps: [pinnedIds.clusterId, p.regionId, p.districtId, p.clusterId],
        },
    );

    useEffect(() => {
        if (p.value && fetchCollectionCentersT.data?.length) {
            const cc = fetchCollectionCentersT?.data?.find(
                cc => cc.id === p.value,
            );
            p.onChange?.(cc);
        } else if (
            !p.value &&
            p.loadCCfromClusterId &&
            pinnedIds.clusterId &&
            fetchCollectionCentersT.data?.length === 1
        ) {
            const cc = fetchCollectionCentersT?.data?.[0];
            p.onChange?.(cc);
        }
    }, [fetchCollectionCentersT.data?.length]);

    const data = useMemo(
        () =>
            fetchCollectionCentersT?.data?.map(item => ({
                label: item.name,
                value: item.id,
            })),
        [fetchCollectionCentersT.data],
    );

    return (
        <ComboboxSelect
            placeholder="Select Collection Center"
            items={data ?? []}
            value={p.value ? [p.value] : p.defaultValue ? [p.defaultValue] : []}
            caretAs={p.customCaret}
            comboRootProps={{
                disabled: p.disabled,
                width: p.width ?? "20%",
                ...p.comboRootProps,
            }}
            onChange={selectedIds => {
                const selectedId = selectedIds[0] || null;
                const cc = selectedId
                    ? fetchCollectionCentersT?.data?.find(
                          item => item.id === selectedId,
                      )
                    : null;
                p.onChange?.(cc);
            }}
            width={p.width ?? "20%"}
            placement={p.placement}
            portalled={p.portalled ?? true}
            comboBoxProps={{ ...p.comboBoxProps }}
            comboInputProps={{ ...p.comboInputProps }}
        />
    );
}
